import { DateTransform } from '@/shared/utilities/class-validator-extends';
import { Transform, Type } from 'class-transformer';
import { IsArray, IsBoolean, IsEnum, IsObject, IsOptional, IsString, IsUUID } from 'class-validator';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { Attachment } from './attachment';
import { MessageContentType } from './message-content-type';
import type { ReactionSummary } from './message-reaction';
import { MessageStatus } from './message-status';

export class Message {
  @IsUUID()
  messageId: string;

  @IsUUID()
  conversationId: string;

  @IsUUID()
  senderId: string;

  bucketId: string;

  @IsString()
  senderName: string;

  @IsString()
  content: string;

  @IsEnum(MessageContentType)
  contentType: MessageContentType;

  @IsOptional()
  @IsUUID()
  parentMessageId: string | null;

  @IsOptional()
  @IsUUID()
  originalMessageId: string | null;

  @Transform(DateTransform.toClass, { toClassOnly: true })
  @Transform(DateTransform.toPlain, { toPlainOnly: true })
  @Type(() => String)
  sentAt: Dayjs;

  @IsObject()
  reactions: Record<string, number>;

  @IsOptional()
  @IsObject()
  reactionSummary?: ReactionSummary;

  @IsBoolean()
  isRevoked: boolean;

  @IsOptional()
  @Transform(DateTransform.toClass, { toClassOnly: true })
  @Transform(DateTransform.toPlain, { toPlainOnly: true })
  @Type(() => String)
  revokedAt: Dayjs | null;

  @Transform(DateTransform.toClass, { toClassOnly: true })
  @Transform(DateTransform.toPlain, { toPlainOnly: true })
  @Type(() => String)
  updatedAt: Dayjs;

  @IsArray()
  structuredAttachments?: Nullable<Attachment[]>;

  @IsBoolean()
  pinned: boolean;

  @IsOptional()
  @Transform(DateTransform.toClass, { toClassOnly: true })
  @Transform(DateTransform.toPlain, { toPlainOnly: true })
  @Type(() => String)
  pinnedAt: Dayjs | null;

  @IsOptional()
  @IsUUID()
  pinnedBy: string | null;

  parentMessage?: Message;
  isSending?: boolean = false;
  canRetry?: boolean = false;
  hideAvatar?: boolean = false;
  hideName?: boolean = false;
  showDateMarker?: boolean = false;

  @IsOptional()
  @IsEnum(MessageStatus)
  status?: MessageStatus;

  @IsOptional()
  deliveredCount?: number;

  @IsOptional()
  seenCount?: number;

  constructor(data: Partial<Message> = {}, parentMsg?: Message) {
    this.bucketId = data.bucketId || '';
    this.messageId = data.messageId || '';
    this.conversationId = data.conversationId || '';
    this.senderId = data.senderId || '';
    this.senderName = data.senderName || '';
    this.content = data.content || '';
    this.contentType = data.contentType || MessageContentType.TEXT;
    this.parentMessageId = data.parentMessageId || null;
    this.originalMessageId = data.originalMessageId || null;
    this.sentAt = data.sentAt || dayjs();
    this.reactions = data.reactions || {};
    this.reactionSummary = data.reactionSummary || {};
    this.isRevoked = data.isRevoked || false;
    this.revokedAt = data.revokedAt || null;
    this.updatedAt = data.updatedAt || dayjs();
    this.structuredAttachments = data.structuredAttachments || null;
    this.pinned = data.pinned || false;
    this.pinnedAt = data.pinnedAt || null;
    this.pinnedBy = data.pinnedBy || null;
    this.isSending = data.isSending || false;
    this.status = data.status || MessageStatus.SENT; // Default to SENT if not provided
    this.deliveredCount = data.deliveredCount || 0;
    this.seenCount = data.seenCount || 0;
    this.parentMessage = parentMsg;
  }
}
